import {
  ApiGameEvents,
  GameEvent,
  GameEventType,
  getGameEventType,
} from "./GameEvent";
import { GamePoint } from "./GamePoint";

export function getGameEventsGroupedByPoint(
  apiGameEvents: ApiGameEvents,
  homeTeamID: string,
  awayTeamID: string,
): GamePoint[] {
  const points: GamePoint[] = [];

  const { homeEvents, awayEvents } = apiGameEvents;

  // Iterate through one team's events to identify the start of each point, and create a new GamePoint for each. We assume that the events are in chronological order, and that the first event of a point will always be a point-initiating action by either team.
  let homeTeamStartedOnOffense = null;
  let currentPoint: GamePoint | null = null;
  for (const event of homeEvents) {
    if (getGameEventType(event.type) === GameEventType.START_D_POINT) {
      points.push(GamePoint.createPoint(points.length, homeTeamID, awayTeamID));
      homeTeamStartedOnOffense = false;
      currentPoint = points[points.length - 1];
    } else if (getGameEventType(event.type) === GameEventType.START_O_POINT) {
      points.push(GamePoint.createPoint(points.length, awayTeamID, homeTeamID));
      homeTeamStartedOnOffense = true;
      currentPoint = points[points.length - 1];
    }
    if (homeTeamStartedOnOffense) {
      currentPoint?.addReceivingTeamEvent({
        ...event,
        teamId: homeTeamID,
        eventType: getGameEventType(event.type),
      });
    } else {
      currentPoint?.addPullingTeamEvent({
        ...event,
        teamId: homeTeamID,
        eventType: getGameEventType(event.type),
      });
    }
  }

  // Now, iterate through the other team's events and add them to the appropriate point. We can determine which point an event belongs to based on the order of the events, and whether the other team started on offense or defense for that point.
  let pointCounter = 0;
  let awayTeamStartedOnOffense = null;
  for (const event of awayEvents) {
    if (
      getGameEventType(event.type) === GameEventType.START_D_POINT ||
      getGameEventType(event.type) === GameEventType.START_O_POINT
    ) {
      currentPoint = points[pointCounter];
      pointCounter++;
    }
    if (getGameEventType(event.type) === GameEventType.START_D_POINT) {
      awayTeamStartedOnOffense = false;
    } else if (getGameEventType(event.type) === GameEventType.START_O_POINT) {
      awayTeamStartedOnOffense = true;
    }
    if (awayTeamStartedOnOffense) {
      currentPoint?.addReceivingTeamEvent({
        ...event,
        teamId: awayTeamID,
        eventType: getGameEventType(event.type),
      });
    } else {
      currentPoint?.addPullingTeamEvent({
        ...event,
        teamId: awayTeamID,
        eventType: getGameEventType(event.type),
      });
    }
  }
  return points;
}

export function assertExists(value: any, message: string): asserts value {
  if (value === null || value === undefined) {
    throw new Error(message);
  }
}

const SET_COLORS = {
  GOAL_COLOR: "rgba(0, 143, 0, 0.95)",
  TURNOVER_COLOR: "rgba(255, 0, 0, 0.95)",
  IN_BOUNDS_PULL_COLOR: "rgba(53, 211, 255, 0.95)",
  OUT_OF_BOUNDS_PULL_COLOR: "rgba(0, 0, 255, 0.95)",
};

const TEAM_COLOR_MAP: {
  [teamId: string]: { pass: string };
} = {
  spiders: {
    pass: "rgba(255, 215, 0, 0.95)", // gold
  },
  empire: {
    pass: "rgba(108, 206, 60, 0.95)", // light green
  },
};

// Team color helpers (hard-coded shortcuts)
export function getEventColor(event: GameEvent) {
  if (!event) return "rgba(200,200,200,0.95)";
  const key = String(event.teamId).toLowerCase();
  const colorMap = TEAM_COLOR_MAP[key];
  switch (event.eventType) {
    case GameEventType.PASS:
      return colorMap.pass;
    case GameEventType.GOAL:
      return SET_COLORS.GOAL_COLOR;
    case GameEventType.PULL_INBOUNDS:
      return SET_COLORS.IN_BOUNDS_PULL_COLOR;
    case GameEventType.PULL_OUT_OF_BOUNDS:
      return SET_COLORS.OUT_OF_BOUNDS_PULL_COLOR;
    case GameEventType.DROP:
    case GameEventType.THROWAWAY_THROWN_BY_RECORDING_TEAM:
    case GameEventType.CALLAHAN_THROWN_BY_RECORDING_TEAM:
    case GameEventType.STALL_AGAINST_RECORDING_TEAM:
    case GameEventType.OFFSIDES_RECORDING_TEAM:
      return SET_COLORS.TURNOVER_COLOR;
    default:
      return "rgba(200,200,200,0.95)"; // default
  }
}

export function getEventPlayers(
  event: GameEvent,
): { name: string; action: string }[] {
  const players: { name: string; action: string }[] = [];
  if (event.thrower) {
    players.push({
      name: event.thrower,
      action:
        event.eventType === GameEventType.PASS
          ? "throws to"
          : event.eventType === GameEventType.THROWAWAY_THROWN_BY_RECORDING_TEAM
            ? "turnover"
            : "",
    });
  }
  if (event.receiver) {
    players.push({ name: event.receiver, action: "" });
  }
  if (event.puller) {
    players.push({ name: event.puller, action: "pulls" });
  }
  if (event.line) {
    for (const player of event.line) {
      players.push({ name: player, action: "" });
    }
  }
  if (event.defender) {
    players.push({ name: event.defender, action: "block" });
  }
  return players;
}
